import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Collapse, Spinner, Table } from 'react-bootstrap';
import Select, { OnChangeValue, StylesConfig } from 'react-select';
import { useTranslation } from 'react-i18next';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { faCheck, faChevronDown, faChevronRight, faExclamationTriangle, faPrint, faTrash, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../../components/CustomToastContent';
import { InfoModal } from '../../components/InfoModal';
import { NoDataComponent } from '../../components/NoDataComponent';
import { PaginationComponent } from '../../components/Pagination';
import { TableComponentProps, TableData, Warehouse } from '../../models/table-models';
import {
    HandoutRequest,
    HandoutRequestItem,
    HandoutRequestsRevision,
    HandoutRequestStatus,
    HandoutReservationResponse,
    ItemQuantity,
} from '../../models/warehouse-models';
import { TableRequestParams, useTableData } from '../../utils/data-hooks';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import {
    deleteHandoutRequest,
    getHandoutRequest,
    getHandoutPreparation,
    getHandoutRequestsRevision,
    issuePreparedHandoutRequest,
    prepareHandoutRequest,
    releaseHandoutPreparation,
    releaseHandoutReservation,
    reserveHandoutQuantity,
    useGetAllCompletedHandouts,
    useGetAllQuantities as getAllQuantities,
    useGetHandoutRequests,
    useGetWarehouses,
} from '../../utils/warehouses-hooks';
import { transformDateToString } from '../../utils/helper-functions';

interface SelectOption {
    label: string;
    value: string;
}

const statusVariant: Record<HandoutRequestStatus, string> = {
    PENDING: 'warning',
    PREPARING: 'light',
    APPROVED: 'success',
    DECLINED: 'danger',
    PARTIALLY_APPROVED: 'info',
};

const formatValue = (value?: string | number | null) => value ?? '-';

const warehouseRequestSelectStyles: StylesConfig<SelectOption, false> = {
    container: (base) => ({
        ...base,
        minWidth: '280px',
        width: '320px',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 99999,
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 99999,
    }),
};

type RequestReservationState = {
    isReserving: boolean;
    reservations: ReservedRequestQuantity[];
    availabilityByItemId?: Record<number, boolean>;
    payloadsByItemId?: Record<number, QuantityReservationPayload[]>;
    matchedQuantityByItemId?: Record<number, ItemQuantity>;
    warning?: string;
    requestSnapshot?: HandoutRequest;
    preparationToken?: string;
    isPrepared?: boolean;
    preparedAllocationLabelsByItemId?: Record<number, string>;
};

type QuantityReservationPayload = {
    requestItemId: number;
    quantity: ItemQuantity;
    count: number;
};

type ReservedRequestQuantity = QuantityReservationPayload & {
    reservation: HandoutReservationResponse;
};

type HandoutRequestWithIds = HandoutRequest & {
    requestIds: number[];
};

type HandoutRequestItemWithId = HandoutRequestItem & {
    id: number;
};

const hasRequestItemId = (requestItem: HandoutRequestItem): requestItem is HandoutRequestItemWithId =>
    requestItem.id != null;

/**
 * The previous signature was built from the request count plus the id, createdAt and status of the
 * first request, so everything the distributor does to an item from the phone left it unchanged -
 * an edited count, a dropped position, an item merged into an existing request - and the table kept
 * showing stale numbers. The server now reports when a request last changed, which covers all of it.
 */
const getRevisionSignature = (revision: HandoutRequestsRevision): string =>
    `${revision.totalCount}:${revision.lastUpdatedAt ?? ''}`;

const mapHandoutRequestsToRows = (requests: HandoutRequest[]): HandoutRequestWithIds[] =>
    requests
        .filter((request) => request.id != null)
        .map((request) => ({
            ...request,
            requestIds: [request.id as number],
        }));


const getRequestItemPassportId = (requestItem: HandoutRequestItem) =>
    requestItem.passportId ?? requestItem.itemId;

const getRequestedQuantityCount = (requestItem: HandoutRequestItem) => {
    return requestItem.itemCount;
};

const isMatchingQuantityForRequestItem = (quantity: ItemQuantity, requestItem: HandoutRequestItem) => {
    if (!quantity.id) return false;

    return quantity.passport.passportId === getRequestItemPassportId(requestItem);
};

const formatQuantityName = (quantity: ItemQuantity) =>
    `${quantity.passport.passport || quantity.passport.name} (${quantity.batchCode})`;

const formatQuantityPayloads = (
    payloads?: QuantityReservationPayload[],
    matchedQuantity?: ItemQuantity,
    requestedCount?: number,
) => {
    if (!payloads?.length && !matchedQuantity) return '-';
    if (!payloads?.length && matchedQuantity) return `${formatQuantityName(matchedQuantity)} x ${requestedCount ?? 0}`;
    if (!payloads) return '-';

    return (payloads ?? [])
        .map(({ quantity, count }) => `${formatQuantityName(quantity)} x ${count}`)
        .join(', ');
};

const renderRequestStatus = (status: HandoutRequestStatus, translate: (key: string) => string) => (
    <Badge
        bg={statusVariant[status]}
        className={`status-badge-bordered ${status === 'PREPARING' ? 'status-badge-preparing' : ''}`}
    >
        {translate(status)}
    </Badge>
);

interface HandoutRequestPrintLabels {
    count: string;
    createdAt: string;
    distributor: string;
    name: string;
    oilQuantity: string;
    report: string;
    status: string;
    type: string;
    warehouse: string;
    willBeIssued: string;
}

const escapeHtml = (value: string | number) => {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
};

const printHandoutRequest = (
    request: HandoutRequest,
    warehouseName: string | undefined,
    reservationState: RequestReservationState | undefined,
    labels: HandoutRequestPrintLabels,
    translate: (key: string) => string,
) => {
    const rowsHtml = request.items
        .map((item) => {
            const itemId = item.id;

            return `
                <tr>
                    <td class="name-cell">${escapeHtml(formatValue(item.passportName))}</td>
                    <td>${escapeHtml(translate(item.itemType))}</td>
                    <td>${escapeHtml(formatValue(item.itemCount))}</td>
                    <td>${escapeHtml(formatValue(item.oilQuantity))}</td>
                    <td class="allocation-cell">${escapeHtml(
                        (itemId != null ? reservationState?.preparedAllocationLabelsByItemId?.[itemId] : undefined)
                        ?? formatQuantityPayloads(
                            itemId != null ? reservationState?.payloadsByItemId?.[itemId] : undefined,
                            itemId != null ? reservationState?.matchedQuantityByItemId?.[itemId] : undefined,
                            getRequestedQuantityCount(item),
                        )
                    )}</td>
                </tr>
            `;
        })
        .join('');
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);

    const printWindow = printFrame.contentWindow;
    const printDocument = printFrame.contentDocument ?? printWindow?.document;
    if (!printWindow || !printDocument) {
        printFrame.remove();
        return;
    }

    const removeFrame = () => {
        setTimeout(() => printFrame.remove(), 0);
    };

    printWindow.onafterprint = removeFrame;
    printDocument.open();
    printDocument.write(`
        <!doctype html>
        <html>
            <head>
                <title></title>
                <style>
                    @page {
                        size: A4 portrait;
                        margin: 0;
                    }
                    html,
                    body {
                        width: 210mm;
                        margin: 0;
                        padding: 0;
                    }
                    body {
                        font-family: Arial, sans-serif;
                        color: #111;
                        font-size: 16px;
                        line-height: 1.35;
                    }
                    .print-page {
                        box-sizing: border-box;
                        width: 210mm;
                        min-height: 297mm;
                        padding: 10mm;
                    }
                    h1 {
                        margin: 0 0 16px;
                        font-size: 28px;
                        line-height: 1.2;
                    }
                    .meta {
                        display: grid;
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 8px 24px;
                        margin-bottom: 22px;
                        font-size: 16px;
                    }
                    table {
                        box-sizing: border-box;
                        width: 100%;
                        border-collapse: collapse;
                        table-layout: auto;
                    }
                    th,
                    td {
                        border: 1px solid #666;
                        padding: 8px 10px;
                        text-align: left;
                        vertical-align: top;
                        white-space: nowrap;
                    }
                    .allocation-cell,
                    th:last-child {
                        white-space: nowrap;
                    }
                    th {
                        background: #e9ecef;
                        font-weight: 700;
                        font-size: 14px;
                    }
                    tbody td {
                        font-size: var(--row-font-size, 14px);
                    }
                    tbody tr:nth-child(even) {
                        background: #f8f9fa;
                    }
                    thead {
                        display: table-header-group;
                    }
                    tr {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                </style>
            </head>
            <body>
                <main class="print-page">
                    <h1>${escapeHtml(labels.report)}</h1>
                    <div class="meta">
                        <div><strong>${escapeHtml(labels.distributor)}:</strong> ${escapeHtml(formatValue(request.distributorName))}</div>
                        <div><strong>${escapeHtml(labels.warehouse)}:</strong> ${escapeHtml(formatValue(warehouseName))}</div>
                        <div><strong>${escapeHtml(labels.createdAt)}:</strong> ${escapeHtml(transformDateToString(request.createdAt))}</div>
                        <div><strong>${escapeHtml(labels.status)}:</strong> ${escapeHtml(translate(request.status))}</div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th class="name-cell">${escapeHtml(labels.name)}</th>
                                <th>${escapeHtml(labels.type)}</th>
                                <th>${escapeHtml(labels.count)}</th>
                                <th>${escapeHtml(labels.oilQuantity)}</th>
                                <th>${escapeHtml(labels.willBeIssued)}</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${rowsHtml}
                        </tbody>
                    </table>
                </main>
            </body>
        </html>
    `);
    printDocument.close();
    printWindow.focus();
    setTimeout(() => {
        const printPage = printDocument.querySelector<HTMLElement>('.print-page');
        const table = printDocument.querySelector<HTMLTableElement>('table');
        if (printPage && table) {
            const pageStyle = printWindow.getComputedStyle(printPage);
            const availableWidth = printPage.clientWidth
                - parseFloat(pageStyle.paddingLeft)
                - parseFloat(pageStyle.paddingRight);
            let rowFontSize = 14;

            while (table.getBoundingClientRect().width > availableWidth && rowFontSize > 6) {
                rowFontSize -= 0.5;
                table.style.setProperty('--row-font-size', `${rowFontSize}px`);
            }
        }
        printWindow.print();
        setTimeout(removeFrame, 1000);
    }, 250);
};

const HandoutRequestsTable = ({
    getEntities,
    pagination,
    refreshRequests,
}: {
    getEntities: () => TableData<HandoutRequest[]>;
    pagination: TableComponentProps['pagination'];
    refreshRequests: () => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tableT } = useTranslation('translation', { keyPrefix: 'table' });
    const { items, pager } = getEntities();
    const requestRows = useMemo(() => mapHandoutRequestsToRows(items), [items]);
    const [expandedRows, setExpandedRows] = useState<number[]>([]);
    const [selectedWarehouseByRequestId, setSelectedWarehouseByRequestId] = useState<Record<number, string>>({});
    const [reservationStateByRequestId, setReservationStateByRequestId] = useState<Record<number, RequestReservationState>>({});
    const [issuingRequestIds, setIssuingRequestIds] = useState<number[]>([]);
    const [deletingRequestIds, setDeletingRequestIds] = useState<number[]>([]);
    const [requestToDelete, setRequestToDelete] = useState<HandoutRequestWithIds>();
    const [excludedItemIdsByRequestId, setExcludedItemIdsByRequestId] = useState<Record<number, number[]>>({});
    const reservationStateRef = useRef<Record<number, RequestReservationState>>({});
    const [getWarehouses] = useAsyncResource(useGetWarehouses, { 'pager.page': 1, 'pager.itemsPerPage': 1000 });
    const { items: warehouses } = getWarehouses();
    const warehouseOptions = useMemo<SelectOption[]>(
        () =>
            warehouses
                .filter((warehouse: Warehouse) => !warehouse.type)
                .map((warehouse: Warehouse) => ({
                    label: warehouse.warehouseName ?? '',
                    value: String(warehouse.id),
                })),
        [warehouses],
    );
    const printLabels: HandoutRequestPrintLabels = {
        count: tableT('count'),
        createdAt: tableT('createdAt'),
        distributor: tableT('distributor'),
        name: tableT('name'),
        oilQuantity: tableT('oilQuantity'),
        report: t('handoutRequests'),
        status: tableT('status'),
        type: tableT('type'),
        warehouse: tableT('warehouse'),
        willBeIssued: tableT('willBeIssued'),
    };

    const setRequestReservationState = (requestId: number, reservationState: RequestReservationState) => {
        reservationStateRef.current = {
            ...reservationStateRef.current,
            [requestId]: reservationState,
        };
        setReservationStateByRequestId(reservationStateRef.current);
    };

    const releaseRequestReservations = async (requestId: number) => {
        const currentState = reservationStateRef.current[requestId];
        const currentReservations = currentState?.reservations ?? [];

        if (currentState?.isPrepared) {
            await releaseHandoutPreparation(requestId);
            setRequestReservationState(requestId, { isReserving: false, reservations: [] });
            setSelectedWarehouseByRequestId((selectedWarehouses) => {
                const nextWarehouses = { ...selectedWarehouses };
                delete nextWarehouses[requestId];
                return nextWarehouses;
            });
            refreshHandoutRequestData();
            return;
        }

        if (!currentReservations.length) return;

        setRequestReservationState(requestId, {
            isReserving: false,
            reservations: [],
        });

        await Promise.allSettled(
            currentReservations.map(({ reservation }) => releaseHandoutReservation(reservation.reservationId))
        );
    };

    const clearRequestState = (requestId: number) => {
        setRequestReservationState(requestId, {
            isReserving: false,
            reservations: [],
        });
        setSelectedWarehouseByRequestId((selectedWarehouses) => {
            const nextWarehouses = { ...selectedWarehouses };
            delete nextWarehouses[requestId];
            return nextWarehouses;
        });
    };

    const refreshHandoutRequestData = () => {
        resourceCache(getAllQuantities).clear();
        resourceCache(useGetAllCompletedHandouts).clear();
        resourceCache(useGetHandoutRequests).clear();
        refreshRequests();
    };

    const getExcludedItemIds = (requestId?: number) => requestId ? excludedItemIdsByRequestId[requestId] ?? [] : [];

    const toggleRow = (requestId?: number) => {
        if (!requestId) return;
        setExpandedRows((rows) =>
            rows.includes(requestId) ? rows.filter((id) => id !== requestId) : [...rows, requestId],
        );
    };

    const onRequestWarehouseChange = async (
        request: HandoutRequestWithIds,
        option: OnChangeValue<SelectOption, false>,
    ) => {
        const requestId = request.id;
        if (!requestId) return;

        setSelectedWarehouseByRequestId((selectedWarehouses) => ({
            ...selectedWarehouses,
            [requestId]: option?.value ?? '',
        }));

        await releaseRequestReservations(requestId);
        if (!option?.value) {
            setRequestReservationState(requestId, { isReserving: false, reservations: [] });
            return;
        }

        try {
            const freshRequest = await getHandoutRequest(requestId);
            if (freshRequest.status !== 'PENDING') {
                refreshHandoutRequestData();
                return;
            }
            await previewRequestQuantities({ ...freshRequest, requestIds: [requestId] }, option.value);
        } catch {
            clearRequestState(requestId);
            refreshHandoutRequestData();
            toast.error(<CustomToastContent content={[tableT('handoutRequestChanged')]} />, toastOptionsTopRight);
        }
    };

    const getQuantityReservationsForRequest = (
        request: HandoutRequestWithIds,
        quantities: ItemQuantity[],
        excludedItemIds: number[] = [],
    ): {
        payloads: QuantityReservationPayload[];
        availabilityByItemId: Record<number, boolean>;
        payloadsByItemId: Record<number, QuantityReservationPayload[]>;
        matchedQuantityByItemId: Record<number, ItemQuantity>;
        missingCount: number;
    } => {
        let missingCount = 0;
        const availabilityByItemId: Record<number, boolean> = {};
        const payloadsByItemId: Record<number, QuantityReservationPayload[]> = {};
        const matchedQuantityByItemId: Record<number, ItemQuantity> = {};
        const availableCountsByQuantityId = quantities.reduce<Record<number, number>>((availableCounts, quantity) => {
            if (!quantity.id) return availableCounts;

            return {
                ...availableCounts,
                [quantity.id]: quantity.count,
            };
        }, {});

        const payloads = request.items
            .filter(hasRequestItemId)
            .filter((requestItem) => !excludedItemIds.includes(requestItem.id))
            .flatMap((requestItem) => {
                let remainingCount = getRequestedQuantityCount(requestItem);
                availabilityByItemId[requestItem.id] = false;
                payloadsByItemId[requestItem.id] = [];
                const matchingQuantities = quantities.filter((quantity) =>
                    isMatchingQuantityForRequestItem(quantity, requestItem)
                );
                const matchedQuantity = matchingQuantities[0];
                if (matchedQuantity) {
                    matchedQuantityByItemId[requestItem.id] = matchedQuantity;
                }
                const availableMatchingQuantities = matchingQuantities.filter(
                    (quantity) => quantity.id != null && availableCountsByQuantityId[quantity.id] > 0
                );

                const itemPayloads = availableMatchingQuantities.reduce<QuantityReservationPayload[]>((quantityPayloads, quantity) => {
                    if (remainingCount <= 0 || quantity.id == null) return quantityPayloads;

                    const availableCount = availableCountsByQuantityId[quantity.id];
                    const countToReserve = Math.min(availableCount, remainingCount);
                    remainingCount -= countToReserve;
                    availableCountsByQuantityId[quantity.id] -= countToReserve;

                    return [
                        ...quantityPayloads,
                        {
                            requestItemId: requestItem.id,
                            quantity,
                            count: countToReserve,
                        },
                    ];
                }, []);

                payloadsByItemId[requestItem.id] = itemPayloads;

                if (remainingCount > 0) {
                    missingCount += remainingCount;
                }

                availabilityByItemId[requestItem.id] = remainingCount === 0;

                return itemPayloads;
            });

        return { payloads, availabilityByItemId, payloadsByItemId, matchedQuantityByItemId, missingCount };
    };

    const previewRequestQuantities = async (
        request: HandoutRequestWithIds,
        warehouseId: string,
        excludedItemIds = getExcludedItemIds(request.id),
    ) => {
        if (!request.id) return;

        setRequestReservationState(request.id, {
            isReserving: true,
            reservations: [],
        });

        try {
            const quantitiesResponse = await getAllQuantities(
                { filter: { id: warehouseId } } as TableRequestParams,
                true,
            );
            const {
                payloads,
                availabilityByItemId,
                payloadsByItemId,
                matchedQuantityByItemId,
                missingCount,
            } = getQuantityReservationsForRequest(request, quantitiesResponse.items, excludedItemIds);

            if (missingCount > 0 || payloads.length === 0) {
                setRequestReservationState(request.id, {
                    isReserving: false,
                    reservations: [],
                    availabilityByItemId,
                    payloadsByItemId,
                    matchedQuantityByItemId,
                    warning: tableT('insufficientWarehouseQuantity'),
                });
                return;
            }

            setRequestReservationState(request.id, {
                isReserving: false,
                reservations: [],
                availabilityByItemId,
                payloadsByItemId,
                matchedQuantityByItemId,
                requestSnapshot: request,
            });
        } catch {
            setRequestReservationState(request.id, {
                isReserving: false,
                reservations: [],
                availabilityByItemId: request.items
                    .filter(hasRequestItemId)
                    .reduce<Record<number, boolean>>((availableItems, item) => ({
                        ...availableItems,
                        [item.id]: false,
                    }), {}),
                payloadsByItemId: {},
                matchedQuantityByItemId: {},
                warning: tableT('insufficientWarehouseQuantity'),
            });

        }
    };

    const toggleExcludedRequestItem = (request: HandoutRequestWithIds, itemId: number) => {
        if (!request.id) return;
        if (reservationStateRef.current[request.id]?.isPrepared) return;

        const requestId = request.id;
        const currentExcludedItemIds = excludedItemIdsByRequestId[requestId] ?? [];
        const nextExcludedItemIds = currentExcludedItemIds.includes(itemId)
            ? currentExcludedItemIds.filter((currentItemId) => currentItemId !== itemId)
            : [...currentExcludedItemIds, itemId];

        setExcludedItemIdsByRequestId((excludedItems) => ({
            ...excludedItems,
            [requestId]: nextExcludedItemIds,
        }));

        releaseRequestReservations(requestId).then(() => {
            const warehouseId = selectedWarehouseByRequestId[requestId];
            if (warehouseId) {
                const snapshot = reservationStateRef.current[requestId]?.requestSnapshot ?? request;
                previewRequestQuantities({ ...snapshot, requestIds: [requestId] }, warehouseId, nextExcludedItemIds);
            }
        });
    };

    const prepareAndPrintRequest = async (
        request: HandoutRequestWithIds,
        warehouseName?: string,
    ) => {
        if (!request.id) return;
        const requestId = request.id;
        const reservationState = reservationStateRef.current[requestId];
        if (reservationState?.isPrepared && reservationState.requestSnapshot) {
            printHandoutRequest(reservationState.requestSnapshot, warehouseName, reservationState, printLabels, t);
            return;
        }
        const previewPayloads = Object.values(reservationState?.payloadsByItemId ?? {}).flat();
        if (!previewPayloads.length || reservationState?.warning) {
            toast.error(<CustomToastContent content={[tableT('reservationRequired')]} />, toastOptionsTopRight);
            return;
        }

        setIssuingRequestIds((requestIds) => [...requestIds, requestId]);
        const reservations: ReservedRequestQuantity[] = [];
        try {
            const snapshot = reservationState.requestSnapshot ?? request;
            const freshRequest = await getHandoutRequest(requestId);
            if (freshRequest.status !== 'PENDING' || freshRequest.version !== snapshot.version) {
                throw new Error('HANDOUT_REQUEST_CHANGED');
            }
            const warehouseId = selectedWarehouseByRequestId[requestId];
            const quantitiesResponse = await getAllQuantities(
                { filter: { id: warehouseId } } as TableRequestParams,
                true,
            );
            const allocationPlan = getQuantityReservationsForRequest(
                { ...freshRequest, requestIds: [requestId] },
                quantitiesResponse.items,
                getExcludedItemIds(requestId),
            );
            if (allocationPlan.missingCount > 0 || allocationPlan.payloads.length === 0) {
                throw new Error('INSUFFICIENT_WAREHOUSE_QUANTITY');
            }
            for (const payload of allocationPlan.payloads) {
                if (!payload.quantity.id) continue;
                const reservation = await reserveHandoutQuantity({
                    quantityId: payload.quantity.id,
                    passportId: payload.quantity.passport.passportId,
                    warehouseId: Number(warehouseId),
                    batchCode: payload.quantity.batchCode,
                    count: payload.count,
                    forUserId: freshRequest.userId,
                });
                reservations.push({ ...payload, reservation });
            }
            const preparation = await prepareHandoutRequest(
                requestId,
                freshRequest.version,
                Number(warehouseId),
                reservations.map(({ requestItemId, reservation }) => ({
                    requestItemId,
                    reservationId: reservation.reservationId,
                })),
            );
            const preparedState: RequestReservationState = {
                ...reservationState,
                reservations,
                availabilityByItemId: allocationPlan.availabilityByItemId,
                payloadsByItemId: allocationPlan.payloadsByItemId,
                matchedQuantityByItemId: allocationPlan.matchedQuantityByItemId,
                requestSnapshot: preparation.request,
                preparationToken: preparation.preparationToken,
                isPrepared: true,
            };
            setRequestReservationState(requestId, preparedState);
            printHandoutRequest(preparation.request, warehouseName, preparedState, printLabels, t);
            refreshHandoutRequestData();
        } catch {
            await Promise.allSettled(
                reservations.map(({ reservation }) => releaseHandoutReservation(reservation.reservationId))
            );
            clearRequestState(requestId);
            refreshHandoutRequestData();
            toast.error(<CustomToastContent content={[tableT('handoutRequestChanged')]} />, toastOptionsTopRight);
        } finally {
            setIssuingRequestIds((requestIds) => requestIds.filter((id) => id !== requestId));
        }
    };

    const issueRequest = async (request: HandoutRequestWithIds) => {
        if (!request.id) return;

        const reservationState = reservationStateRef.current[request.id];
        if (!reservationState?.isPrepared || !reservationState.preparationToken) {
            toast.error(<CustomToastContent content={[tableT('reservationRequired')]} />, toastOptionsTopRight);
            return;
        }

        setIssuingRequestIds((requestIds) => [...requestIds, request.id as number]);

        try {
            await issuePreparedHandoutRequest(request.id, reservationState.preparationToken);

            clearRequestState(request.id);
            refreshHandoutRequestData();
            toast.success(<CustomToastContent content={[t('handoutSuccess')]} />, toastOptionsTopRight);
        } catch {
            toast.error(<CustomToastContent content={[tableT('insufficientWarehouseQuantity')]} />, toastOptionsTopRight);
        } finally {
            setIssuingRequestIds((requestIds) => requestIds.filter((requestId) => requestId !== request.id));
        }
    };

    const deleteRequest = async (request: HandoutRequestWithIds) => {
        if (!request.id) return;

        setDeletingRequestIds((requestIds) => [...requestIds, request.id as number]);

        try {
            await releaseRequestReservations(request.id);
            await Promise.all(request.requestIds.map((requestId) => deleteHandoutRequest(requestId)));

            clearRequestState(request.id);
            refreshHandoutRequestData();
            toast.success(<CustomToastContent content={[t('handoutRequestDeleted')]} />, toastOptionsTopRight);
        } catch {
            toast.error(<CustomToastContent content={[t('generalError')]} />, toastOptionsTopRight);
        } finally {
            setDeletingRequestIds((requestIds) => requestIds.filter((requestId) => requestId !== request.id));
        }
    };

    const onDeleteModalHide = (isConfirmed: boolean) => {
        const selectedRequest = requestToDelete;
        setRequestToDelete(undefined);

        if (isConfirmed && selectedRequest) {
            deleteRequest(selectedRequest).then();
        }
    };

    useEffect(() => {
        return () => {
            Object.values(reservationStateRef.current).forEach((reservationState) => {
                if (reservationState.isPrepared) return;
                reservationState.reservations.forEach(({ reservation }) => {
                    releaseHandoutReservation(reservation.reservationId).catch((error) =>
                        console.error('Failed to release handout request reservation', error),
                    );
                });
            });
        };
    }, []);

    useEffect(() => {
        requestRows.filter((request) => request.id && request.status === 'PREPARING').forEach((request) => {
            const requestId = request.id as number;
            if (reservationStateRef.current[requestId]?.isPrepared) return;
            void getHandoutPreparation(requestId).then((preparation) => {
                const preparedAllocationLabelsByItemId = preparation.allocations.reduce<Record<number, string>>(
                    (labels, allocation) => ({
                        ...labels,
                        [allocation.requestItemId]: [labels[allocation.requestItemId],
                            `${allocation.batchCode || '-'} x ${allocation.count}`]
                            .filter(Boolean).join(', '),
                    }),
                    {},
                );
                setSelectedWarehouseByRequestId((selected) => ({
                    ...selected,
                    [requestId]: String(preparation.request.warehouseId),
                }));
                setRequestReservationState(requestId, {
                    isReserving: false,
                    reservations: [],
                    requestSnapshot: preparation.request,
                    preparationToken: preparation.preparationToken,
                    isPrepared: true,
                    preparedAllocationLabelsByItemId,
                });
            }).catch(() => undefined);
        });
    }, [requestRows]);

    if (!requestRows.length) {
        return <NoDataComponent noDataText={t('handoutRequests')} />;
    }

    return (
        <div className='w-100'>
            <InfoModal
                show={!!requestToDelete}
                onHide={onDeleteModalHide}
            />
            <Table responsive hover className='table-bordered bg-white w-100'>
                <thead>
                    <tr>
                        <th />
                        <th>{tableT('requestNumber')}</th>
                        <th>{tableT('distributor')}</th>
                        <th>{tableT('status')}</th>
                        <th>{tableT('createdAt')}</th>
                        <th>{tableT('warehouse')}</th>
                        <th>{tableT('actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {requestRows.map((request) => {
                        const rowExpanded = request.id != null && expandedRows.includes(request.id);
                        const selectedWarehouseId = request.id ? selectedWarehouseByRequestId[request.id] : undefined;
                        const selectedWarehouse = warehouseOptions.find((option) => option.value === selectedWarehouseId) ?? null;
                        const reservationState = request.id ? reservationStateByRequestId[request.id] : undefined;
                        const requestReady = (Object.values(reservationState?.payloadsByItemId ?? {}).flat().length > 0
                            || !!reservationState?.isPrepared) && !reservationState?.warning;
                        const requestPrepared = !!reservationState?.isPrepared;
                        const displayedRequest = reservationState?.requestSnapshot ?? request;
                        const isIssuing = !!request.id && issuingRequestIds.includes(request.id);
                        const isDeleting = !!request.id && deletingRequestIds.includes(request.id);

                        return (
                            <React.Fragment key={request.id}>
                                <tr className='cursor-pointer' onClick={() => toggleRow(request.id)}>
                                    <td className='align-middle text-center'>
                                        <FontAwesomeIcon icon={rowExpanded ? faChevronDown : faChevronRight} />
                                    </td>
                                    <td>#{request.id}</td>
                                    <td>{request.distributorName}</td>
                                    <td>{renderRequestStatus(displayedRequest.status, t)}</td>
                                    <td>{transformDateToString(request.createdAt, 'dd/mm/yyyy HH:MM')}</td>
                                    <td onClick={(event) => event.stopPropagation()}>
                                        <div className='d-flex align-items-center gap-2'>
                                            <Select<SelectOption, false>
                                                className='logs-select'
                                                placeholder={t('warehouse')}
                                                isClearable
                                                value={selectedWarehouse}
                                                options={warehouseOptions}
                                                onChange={(option) => onRequestWarehouseChange(request, option)}
                                                isDisabled={requestPrepared || isIssuing || isDeleting}
                                                backspaceRemovesValue
                                                menuPortalTarget={document.body}
                                                styles={warehouseRequestSelectStyles}
                                            />
                                            <span className='d-inline-flex align-items-center justify-content-center' style={{ width: '16px' }}>
                                                {reservationState?.isReserving && <Spinner animation='border' size='sm' />}
                                                {!reservationState?.isReserving && requestReady && (
                                                    <FontAwesomeIcon
                                                        icon={faCheck}
                                                        className='text-success'
                                                        title={requestPrepared ? tableT('quantityReserved') : tableT('quantityAvailableForPrint')}
                                                    />
                                                )}
                                                {!reservationState?.isReserving && reservationState?.warning && (
                                                    <FontAwesomeIcon
                                                        icon={faExclamationTriangle}
                                                        className='text-warning warning-triangle-bordered'
                                                        title={reservationState.warning}
                                                    />
                                                )}
                                            </span>
                                        </div>
                                    </td>
                                    <td onClick={(event) => event.stopPropagation()}>
                                        <div className='d-flex align-items-center justify-content-between gap-2 w-100'>
                                            <Button
                                                variant='success'
                                                disabled={!requestPrepared || isIssuing || isDeleting}
                                                onClick={() => issueRequest(request)}
                                            >
                                                {isIssuing && <Spinner animation='border' size='sm' className='me-2' />}
                                                {t('issue')}
                                            </Button>
                                            <Button
                                                variant='outline-secondary'
                                                disabled={!requestReady || isIssuing || isDeleting}
                                                onClick={() => prepareAndPrintRequest(request, selectedWarehouse?.label)}
                                                title={t('print')}
                                            >
                                                <FontAwesomeIcon icon={faPrint} />
                                            </Button>
                                            <Button
                                                variant='outline-warning'
                                                disabled={!requestPrepared || isIssuing || isDeleting}
                                                onClick={() => releaseRequestReservations(request.id as number)}
                                                title={requestPrepared ? tableT('releasePreparation') : undefined}
                                                aria-hidden={!requestPrepared}
                                                tabIndex={requestPrepared ? 0 : -1}
                                                style={{ visibility: requestPrepared ? 'visible' : 'hidden' }}
                                            >
                                                <FontAwesomeIcon icon={faUnlock} />
                                            </Button>
                                            <Button
                                                variant='outline-danger'
                                                disabled={isIssuing || isDeleting}
                                                onClick={() => setRequestToDelete(request)}
                                                title={t('delete')}
                                            >
                                                {isDeleting ? (
                                                    <Spinner animation='border' size='sm' />
                                                ) : (
                                                    <FontAwesomeIcon icon={faTrash} />
                                                )}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={7} className='p-0 border-0'>
                                        <Collapse in={rowExpanded}>
                                            <div className='p-3 handout-request-items-wrapper'>
                                                <Table responsive size='sm' className='mb-0 table-bordered handout-request-items-table'>
                                                    <thead>
                                                        <tr>
                                                            <th>{tableT('passport')}</th>
                                                            <th>{tableT('type')}</th>
                                                            <th>{tableT('count')}</th>
                                                            <th>{tableT('oilQuantity')}</th>
                                                            <th>{tableT('willBeIssued')}</th>
                                                            <th>{tableT('status')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {request.items.map((item) => {
                                                            const itemId = item.id;
                                                            const isExcluded = request.id
                                                                && itemId != null
                                                                ? getExcludedItemIds(request.id).includes(itemId)
                                                                : false;
                                                            const itemIsAvailable = itemId != null
                                                                && !!reservationState?.availabilityByItemId?.[itemId];

                                                            return (
                                                                <tr
                                                                    key={item.id}
                                                                    className={`${requestPrepared ? '' : 'cursor-pointer'} ${isExcluded ? 'handout-request-item-excluded' : ''}`}
                                                                    onClick={() => !requestPrepared && itemId != null && toggleExcludedRequestItem(request, itemId)}
                                                                >
                                                                    <td>{item.passportName}</td>
                                                                    <td>{t(item.itemType)}</td>
                                                                    <td>{item.itemCount}</td>
                                                                    <td>{formatValue(item.oilQuantity)}</td>
                                                                    <td>
                                                                        {isExcluded
                                                                            ? '-'
                                                                            : formatQuantityPayloads(
                                                                                itemId != null ? reservationState?.payloadsByItemId?.[itemId] : undefined,
                                                                                itemId != null ? reservationState?.matchedQuantityByItemId?.[itemId] : undefined,
                                                                                getRequestedQuantityCount(item),
                                                                            )}
                                                                    </td>
                                                                    <td className='text-center'>
                                                                        {reservationState?.isReserving && selectedWarehouseId && !isExcluded && (
                                                                            <Spinner animation='border' size='sm' />
                                                                        )}
                                                                        {!reservationState?.isReserving && (
                                                                            <FontAwesomeIcon
                                                                                icon={
                                                                                    selectedWarehouseId &&
                                                                                    !isExcluded &&
                                                                                    itemIsAvailable
                                                                                        ? faCheck
                                                                                        : faExclamationTriangle
                                                                                }
                                                                                className={
                                                                                    selectedWarehouseId &&
                                                                                    !isExcluded &&
                                                                                    itemIsAvailable
                                                                                        ? 'text-success'
                                                                                        : 'text-warning warning-triangle-bordered'
                                                                                }
                                                                                title={
                                                                                    selectedWarehouseId &&
                                                                                    !isExcluded &&
                                                                                    itemIsAvailable
                                                                                        ? tableT('quantityReserved')
                                                                                        : tableT('insufficientWarehouseQuantity')
                                                                                }
                                                                            />
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Collapse>
                                    </td>
                                </tr>
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </Table>
            <PaginationComponent recordsCount={pager.totalCount ?? items.length} pagination={pagination} />
        </div>
    );
};

export const HandoutRequests = ({ filter, pagination }: TableComponentProps) => {
    const { currentPage, pageSize } = pagination;
    const requestListSignatureRef = useRef<string>();
    const requestFilter = useMemo(() => ({ ...filter }), [filter]);
    const [getHandoutRequests, fetchHandoutRequests] = useTableData<HandoutRequest[]>(
        useGetHandoutRequests,
        pagination,
        requestFilter,
    );

    const refreshRequests = useCallback(() => {
        resourceCache(useGetHandoutRequests).clear();
        fetchHandoutRequests({
            'pager.page': currentPage,
            'pager.itemsPerPage': pageSize,
            pagination,
            filter: requestFilter,
        });
    }, [currentPage, fetchHandoutRequests, pageSize, pagination, requestFilter]);

    useEffect(() => {
        refreshRequests();
    }, [refreshRequests]);

    useEffect(() => {
        let isActive = true;

        const checkForRequestChanges = () => {
            getHandoutRequestsRevision({ filter: requestFilter })
                .then((revision) => {
                    if (!isActive) return;

                    const nextSignature = getRevisionSignature(revision);
                    if (!requestListSignatureRef.current) {
                        requestListSignatureRef.current = nextSignature;
                        return;
                    }

                    if (requestListSignatureRef.current !== nextSignature) {
                        requestListSignatureRef.current = nextSignature;
                        refreshRequests();
                    }
                })
                .catch(() => {
                    // The table keeps its current state if the lightweight polling request fails.
                });
        };

        const pollingInterval = setInterval(checkForRequestChanges, 30000);

        return () => {
            isActive = false;
            clearInterval(pollingInterval);
        };
    }, [refreshRequests, requestFilter]);

    return (
        <div className='w-100'>
            <Suspense fallback={<Spinner animation='border' />}>
                <HandoutRequestsTable
                    getEntities={getHandoutRequests}
                    pagination={pagination}
                    refreshRequests={refreshRequests}
                />
            </Suspense>
        </div>
    );
};
